import {
    Box,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownTrigger,
    Export,
    IconButton,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import KebabIcon from "components/icons/KebabIcon";
import SubStep from "components/products/bde-home-page/SubStep";
import installBmweStyles from "components/products/bmwe-home-page/install-bmwe.scss";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { LicenseData } from "domain/licenses";
import { BmweOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { OnboardingImage, SoftwarePackage, Versions } from "domain/softwarePackages";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { resolveColor } from "utils/commonFunctions";
import { showShaChecksumToast } from "utils/shaChecksumUtils";

import testIds from "testIds.json";

interface Props {
    stepIndex: number;
    fetchedSoftwarePackages?: SoftwarePackage[];
    fetchedLicenses: LicenseData[];
    featureWorkflowLicensePresent: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    menuExpanded: boolean;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    fetchedOnboardingImages: OnboardingImage[];
    licenses: Versions[];
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
    user: state.userReducer.user,
});

const connector = connect(mapState);

const TRANSLATION_KEYS = {
    TITLE: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.title",
    STEPS: {
        ONE: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepOne",
        TWO: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepTwo",
        THREE: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepThree",
        FOUR: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepFour",
        FOUR_A: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepFourA",
        FOUR_B: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepFourB",
        FIVE: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepFive",
        SIX: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepSix",
        SEVEN: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.stepSeven",
    },
    PRODUCTS: {
        ERASURE_FILE: {
            TITLE: "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.erasureFile",
        },
    },
};

const InstallBmweStep = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const dataType = OnboardingProductType.BMWE + "_" + BmweOnboardingSteps.STEP_INSTALLING_BMWE;

    const onSubmitEventHandler = (productSteps: Onboarding) => {
        props.saveStepInProgressIndex(props.stepIndex);
        props.saveOnboardingStep({
            step: productSteps.step,
            product: productSteps.product,
        });
    };

    const clickEvent = () => {
        if (props.stepClickable) {
            setShowContent(!showContent);
        }
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const bmweImage = props.fetchedOnboardingImages?.find(
        (item) => item.manualProductName === "Blancco Mobile Workspace Edition"
    );

    const handleFileDownload = () => {
        if (props.licenses.length === 1) {
            const link = document.createElement("a");
            const fileLink = props.fetchedOnboardingImages?.find((image) => image.licenseId === 73);
            link.href = fileLink?.manual as string;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleVerifySha = () => {
        const sha = bmweImage?.shaForManual;
        const productName = bmweImage?.manualProductName;

        if (!sha || !productName) {
            return;
        }

        showShaChecksumToast(sha, productName, productName, t, "--theme-icon-fill-default", "--color-white");
    };

    function createContent() {
        return (
            <Box columnLayout>
                <Box columnLayout gap="Md">
                    <SubStep iconNumber={1} translationKey={TRANSLATION_KEYS.STEPS.ONE} subStep hasCards />
                    <Box className={style.portalContent}>
                        <Box
                            justifyContent="SpaceBetween"
                            verticalAlignment="Center"
                            gap="Md"
                            className={installBmweStyles.stepActionButton}
                        >
                            {t("Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.erasureFile")}
                            <Box verticalAlignment="Center" gap="Xxs">
                                <IconButton size="Md" noHoverStyle onClick={handleFileDownload}>
                                    <Export color={"--theme-primary"} />
                                </IconButton>
                                <DropdownMenu
                                    position="bottom"
                                    dropdownOffset="Xxs"
                                    closeOnSelection
                                    flipOptions={{
                                        fallbackPlacements: ["top-start"],
                                    }}
                                >
                                    <DropdownTrigger>
                                        <IconButton size="Md" noHoverStyle>
                                            <KebabIcon color={resolveColor("--theme-icon-fill-default")} />
                                        </IconButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownMenuItem onClick={handleVerifySha}>
                                            {t(
                                                "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.title"
                                            )}
                                        </DropdownMenuItem>
                                    </DropdownContent>
                                </DropdownMenu>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <SubStep iconNumber={2} translationKey={TRANSLATION_KEYS.STEPS.TWO} subStep />

                <SubStep iconNumber={3} translationKey={TRANSLATION_KEYS.STEPS.THREE} subStep />
                <SubStep
                    iconNumber={4}
                    translationKey={TRANSLATION_KEYS.STEPS.FOUR}
                    subStep
                    additionalSteps={[TRANSLATION_KEYS.STEPS.FOUR_A, TRANSLATION_KEYS.STEPS.FOUR_B]}
                />

                <SubStep iconNumber={5} translationKey={TRANSLATION_KEYS.STEPS.FIVE} subStep />
                <SubStep iconNumber={6} translationKey={TRANSLATION_KEYS.STEPS.SIX} subStep />
                <SubStep iconNumber={7} translationKey={TRANSLATION_KEYS.STEPS.SEVEN} subStep />
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <div className={style.templateSelectedBlock}>
                            <StepCompletedText
                                successText={t(
                                    "Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.completed"
                                )}
                                testId={testIds.workArea.onboarding.step.statusLabel}
                                dataType={dataType}
                            />
                        </div>
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                onSubmitEventHandler({
                                    step: BmweOnboardingSteps.STEP_INSTALLING_BMWE,
                                    product: OnboardingProductType.BMWE,
                                });
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={dataType}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.gettingStartedFlow.installBlanccoMobileWorkspaceEdition.markComplete")}
                        </Button>
                    )}
                </div>
            </Box>
        );
    }

    return (
        <StepHeader
            title={t(TRANSLATION_KEYS.TITLE)}
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            clickEvent={clickEvent}
            stepIndex={props.stepIndex}
            showContent={showContent}
            menuExpanded={props.menuExpanded}
            content={createContent()}
            type={OnboardingProductType.BMWE}
            stepName={BmweOnboardingSteps.STEP_INSTALLING_BMWE}
        />
    );
};

export default connector(InstallBmweStep);
